import React from 'react';
import mustache from 'mustache';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickTypes,
  NarvarClickSubTypes,
} from '../../../../../hooks/Analytics';
import { NotificationPreferences } from '../../../../../../../__generated__/loma';
import XCircleIcon from '../../../../ui/icons/XCircle/XCircle';
import { useTranslations } from '../../../../../hooks/useTranslations';
import { useSettings } from '../../../../../hooks/useSettings';
import { LayoutTree } from '../../../DynamicLayout';
import { Channels, PreferenceLevels } from '../../types';
import { useGlobalSettings } from '../../../../../hooks/useGlobalSettings';
import ControlView from '../ControlView/ControlView';

const { FeatureLayout } = LayoutTree;

export interface UnsubscribedProps {
  onOptInAgain: () => void;
  featureName?: string;
  layout?: LayoutTree.FeatureLayout;
  channel: Channels;
  notificationPreferencesState?: NotificationPreferences;
  narvarProfileUrl?: string;
}

export interface UnsubscribedChannelProps {
  unsubscribedMessage: string;
  unsubscribedText?: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  eventComponent: string;
  signUpEvent: string;
}

const Unsubscribed = ({
  onOptInAgain,
  featureName,
  layout,
  channel,
  notificationPreferencesState,
  narvarProfileUrl,
}: UnsubscribedProps) => {
  const { retailerName } = useGlobalSettings();
  const { preferenceLevel } = notificationPreferencesState ?? {};
  const retailerLevel = preferenceLevel === PreferenceLevels.RETAILER;
  const { notificationCenter } = useSettings();
  const { continueShoppingUrl = '' } = notificationCenter ?? {};

  const {
    notificationCenterSmsUnsubscribedMessage,
    notificationCenterSmsSignupAgain,
    notificationCenterSmsContinueShopping,
    notificationCenterEmailUnsubscribedMessage,
    notificationCenterEmailSignupAgain,
    notificationCenterEmailContinueShopping,
    notificationCenterSmsUnsubscribedMessageRetailerLevel,
    notificationCenterEmailUnsubscribedMessageRetailerLevel,
    notificationCenterLineContinueShopping,
    notificationCenterLineSignupAgain,
    notificationCenterLineUnsubscribedMessage,
    notificationCenterLineUnsubscribedMessageRetailerLevel,
    notificationCenterSublimeManageYourPreferences,
    notificationCenterSublimeSignUpForUpdates,
    notificationCenterSublimeUnsubscribeTitle,
    notificationCenterSublimeUnsubscribeText,
  } = useTranslations();

  const logEvent = useAnalyticsPost();

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  const smsUnsubscribedChannelProps: UnsubscribedChannelProps = {
    unsubscribedMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterSmsUnsubscribedMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterSmsUnsubscribedMessage) || '',
    primaryButtonText: notificationCenterSmsSignupAgain || '',
    secondaryButtonText: notificationCenterSmsContinueShopping || '',
    eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
    signUpEvent: NarvarClickSubTypes.SMS_SIGNUP_AGAIN,
  };

  const emailUnsubscribedChannelProps: UnsubscribedChannelProps = {
    unsubscribedMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterEmailUnsubscribedMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterEmailUnsubscribedMessage) || '',
    primaryButtonText: notificationCenterEmailSignupAgain || '',
    secondaryButtonText: notificationCenterEmailContinueShopping || '',
    eventComponent: NarvarEventComponents.NTH_PACK_EMAIL_NOTIFICATION_CENTER,
    signUpEvent: NarvarClickSubTypes.EMAIL_SIGNUP_AGAIN,
  };

  const lineUnsubscribedChannelProps: UnsubscribedChannelProps = {
    unsubscribedMessage:
      (retailerLevel
        ? mustache.render(
            notificationCenterLineUnsubscribedMessageRetailerLevel || '',
            { retailerName },
          )
        : notificationCenterLineUnsubscribedMessage) || '',
    primaryButtonText: notificationCenterLineSignupAgain || '',
    secondaryButtonText: notificationCenterLineContinueShopping || '',
    eventComponent: NarvarEventComponents.NTH_PACK_LINE_NOTIFICATION_CENTER,
    signUpEvent: NarvarClickSubTypes.LINE_SIGNUP_AGAIN,
  };

  const sublimeUnsubscribedChannelProps: UnsubscribedChannelProps = {
    unsubscribedMessage: notificationCenterSublimeUnsubscribeTitle ?? '',
    unsubscribedText: notificationCenterSublimeUnsubscribeText ?? '',
    primaryButtonText: notificationCenterSublimeManageYourPreferences ?? '',
    secondaryButtonText: notificationCenterSublimeSignUpForUpdates ?? '',
    eventComponent: NarvarEventComponents.NTH_PACK_NOTIFICATION_CENTER,
    signUpEvent: NarvarClickSubTypes.EMAIL_SIGNUP_AGAIN,
  };

  const unsubscribedChannelProps = {
    [Channels.SMS]: smsUnsubscribedChannelProps,
    [Channels.HIPAA_SMS]: smsUnsubscribedChannelProps,
    [Channels.EMAIL]: emailUnsubscribedChannelProps,
    [Channels.HIPAA_EMAIL]: emailUnsubscribedChannelProps,
    [Channels.LINE]: lineUnsubscribedChannelProps,
    [Channels.SUBLIME]: sublimeUnsubscribedChannelProps,
  };

  const unsubscribeChannels =
    channel === Channels.SMS ||
    channel === Channels.HIPAA_SMS ||
    channel === Channels.EMAIL ||
    channel === Channels.HIPAA_EMAIL ||
    channel === Channels.LINE ||
    channel === Channels.SUBLIME;

  const {
    unsubscribedMessage = '',
    primaryButtonText = '',
    secondaryButtonText = '',
    eventComponent = '',
    signUpEvent = '',
    unsubscribedText = '',
  } = unsubscribeChannels ? unsubscribedChannelProps[channel] : {};

  const handlePrimaryButtonClick = () => {
    if (channel === Channels.SUBLIME && narvarProfileUrl) {
      window.open(`${narvarProfileUrl}/login?type=phone`);
      return;
    }

    logEvent({
      clickCategory: signUpEvent,
      clickNotificationIsOptIn: true,
      clickSubType: signUpEvent,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent,
      eventFeature: eventComponent,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: eventComponent,
    });
    onOptInAgain();
  };

  const handleSecondaryButtonClick = () => {
    if (channel === Channels.SUBLIME) {
      onOptInAgain();
      return;
    }

    if (continueShoppingUrl) {
      logEvent({
        clickCategory: NarvarClickSubTypes.EXTERNAL_RETAILER_LINK,
        clickLinkDestinationUrl: continueShoppingUrl,
        clickSubType: NarvarClickSubTypes.EXTERNAL_RETAILER_LINK,
        clickType: NarvarClickTypes.OUTGOING,
        eventComponent,
        eventFeature: eventComponent,
        eventType: NarvarEventTypes.CLICK,
        feature: featureName,
        pageComponent: eventComponent,
      });
    }
  };

  const showSecondaryButton =
    channel === Channels.SUBLIME || continueShoppingUrl;

  return (
    <ControlView
      channel={channel}
      isLandscape={isLandscape}
      featureName={featureName}
      icon={<XCircleIcon />}
      heading={unsubscribedMessage}
      text={unsubscribedText}
      primaryButton={{
        text: primaryButtonText,
        onClick: handlePrimaryButtonClick,
      }}
      secondaryButton={
        showSecondaryButton
          ? {
              text: secondaryButtonText,
              link: continueShoppingUrl ?? undefined,
              onClick: handleSecondaryButtonClick,
            }
          : undefined
      }
    />
  );
};

export default Unsubscribed;
